import { RefreshCw, Tablet, Bot, Mail, Cloud, FileUp, User } from 'lucide-react';

type CreatedVia = 'manuell' | 'winvalue_sync' | 'kiosk' | 'mcp' | 'email_import' | 'api' | 'csv_import';

type Props = {
  createdAt: string;
  mitarbeiter?: { vorname: string; nachname: string; avatar_url?: string } | null;
  createdVia: CreatedVia;
  variant?: 'mini' | 'detail';
};

const ICONS: Record<CreatedVia, React.ComponentType<{ className?: string }> | null> = {
  manuell:       null,
  winvalue_sync: RefreshCw,
  kiosk:         Tablet,
  mcp:           Bot,
  email_import:  Mail,
  api:           Cloud,
  csv_import:    FileUp,
};

const LABELS: Record<CreatedVia, string> = {
  manuell:       'manuell angelegt',
  winvalue_sync: 'WinValue Auto-Sync',
  kiosk:         'Kiosk-Wizard',
  mcp:           'MCP / KI-Agent',
  email_import:  'E-Mail-Import',
  api:           'API',
  csv_import:    'CSV-Import',
};

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${time}`;
}

function buildTooltip(props: Props): string {
  const dt = formatDateTime(props.createdAt);
  if (props.mitarbeiter && props.createdVia === 'manuell') {
    const name = `${props.mitarbeiter.vorname} ${props.mitarbeiter.nachname}`;
    return `Erstellt von ${name} am ${dt}`;
  }
  switch (props.createdVia) {
    case 'winvalue_sync': return `Automatisch von WinValue importiert am ${dt}`;
    case 'kiosk':         return `Im Kiosk angelegt am ${dt}`;
    case 'mcp':           return `Via MCP / KI-Agent erstellt am ${dt}`;
    case 'email_import':  return `Via E-Mail-Import erstellt am ${dt}`;
    case 'api':           return `Via API erstellt am ${dt}`;
    case 'csv_import':    return `Via CSV-Import erstellt am ${dt}`;
    default:              return `Manuell erstellt am ${dt}`;
  }
}

function getInitials(vorname: string, nachname: string): string {
  return `${vorname[0] ?? ''}${nachname[0] ?? ''}`.toUpperCase();
}

function hashColor(name: string): string {
  const colors = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return colors[Math.abs(h) % colors.length];
}

import React from 'react';

export function ErstellerBadge({ createdAt, mitarbeiter, createdVia, variant = 'mini' }: Props) {
  const tooltip = buildTooltip({ createdAt, mitarbeiter, createdVia });
  const Icon = ICONS[createdVia];
  const label = LABELS[createdVia];

  if (variant === 'mini') {
    if (mitarbeiter && createdVia === 'manuell') {
      const initials = getInitials(mitarbeiter.vorname, mitarbeiter.nachname);
      const bg = hashColor(`${mitarbeiter.vorname}${mitarbeiter.nachname}`);
      return (
        <span
          data-testid="ersteller-badge-mini"
          title={tooltip}
          className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[9px] font-bold flex-shrink-0 cursor-default"
          style={{ backgroundColor: bg }}
        >
          {initials}
        </span>
      );
    }

    if (Icon) {
      return (
        <span
          data-testid="ersteller-badge-mini"
          title={tooltip}
          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-gray-500 flex-shrink-0 cursor-default"
        >
          <Icon className="w-3 h-3" />
        </span>
      );
    }

    return (
      <span
        data-testid="ersteller-badge-mini"
        title={tooltip}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-gray-400 flex-shrink-0 cursor-default"
      >
        <User className="w-3 h-3" />
      </span>
    );
  }

  // detail variant
  const dt = formatDateTime(createdAt);
  let who: React.ReactNode;

  if (mitarbeiter && createdVia === 'manuell') {
    who = (
      <>
        <span className="font-medium">{mitarbeiter.vorname} {mitarbeiter.nachname}</span>
        {' · '}
        {dt}
        {' · '}
        <em>manuell</em>
      </>
    );
  } else {
    who = (
      <>
        <span className="font-medium">{label}</span>
        {' · '}
        {dt}
        {Icon && (
          <>
            {' · '}
            <Icon className="inline w-3 h-3 align-middle" />
          </>
        )}
      </>
    );
  }

  return (
    <span
      data-testid="ersteller-badge-detail"
      title={tooltip}
      className="inline-flex items-center gap-1 text-[11px] text-gray-400 flex-shrink-0"
    >
      Erstellt von {who}
    </span>
  );
}
